import { useState, type ReactNode } from 'react'
import { ActionIcon, Checkbox, Paper, Text, Tooltip } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconChevronDown, IconLayoutSidebarLeftCollapse } from '@tabler/icons-react'
import {
  BILLING_ENTITY_OPTIONS,
  BILLING_STATUS_LABELS,
  BILLING_STATUS_VALUES,
  type BillingStatusValue,
} from '@/pages/Billing/utils'
import {
  PAYMENT_STATUS_FILTER_OPTIONS,
  PROGRESS_NOTE_OPTIONS,
  ROUTING_OPTIONS,
  VISIT_STATUS_OPTIONS,
  countActiveFilters,
  getDateRangeError,
  getDefaultFilters,
  type BillingV2Filters,
} from '../state'

// Copied presentational pieces (pages/Billing filter panel) — decision: no
// legacy↔v2 component imports across page modules.

const CLAIM_STATUS_OPTIONS: ReadonlyArray<{ value: BillingStatusValue; label: string }> =
  BILLING_STATUS_VALUES.map((value) => ({ value, label: BILLING_STATUS_LABELS[value] }))

type FilterSectionProps = {
  title: string
  defaultOpen?: boolean
  children?: ReactNode
}

const FilterSection = ({ title, defaultOpen = false, children }: FilterSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-t border-slate-100 py-3 first:border-t-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-sm font-semibold text-slate-800"
      >
        <span>{title}</span>
        <IconChevronDown
          size={16}
          className={['text-slate-500 transition-transform', isOpen ? 'rotate-180' : ''].join(' ')}
        />
      </button>
      {isOpen && children ? <div className="mt-3">{children}</div> : null}
    </div>
  )
}

type CheckboxListProps<T extends string> = {
  options: ReadonlyArray<{ value: T; label: string }>
  selected: T[]
  onChange: (next: T[]) => void
}

const CheckboxList = <T extends string>({ options, selected, onChange }: CheckboxListProps<T>) => {
  const toggle = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center justify-between gap-2 text-sm text-slate-700"
        >
          <span>{option.label}</span>
          <Checkbox
            size="xs"
            aria-label={option.label}
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
          />
        </label>
      ))}
    </div>
  )
}

const DATE_INPUT_CLASSNAMES = {
  input:
    'h-8 !w-[150px] rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition outline-none focus:border-blue-400',
}

type FilterPanelProps = {
  className?: string
  filters: BillingV2Filters
  onFiltersChange: (next: BillingV2Filters) => void
  /** Collapsed panels render nothing; the table header shows the re-open toggle. */
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export const FilterPanel = ({
  className = '',
  filters,
  onFiltersChange,
  collapsed = false,
  onToggleCollapse,
}: FilterPanelProps) => {
  const hasActiveFilters = countActiveFilters(filters) > 0
  const rangeError = getDateRangeError(filters)

  if (collapsed) {
    return null
  }

  return (
    <Paper
      withBorder
      radius="md"
      className={['flex w-[260px] shrink-0 flex-col px-4 py-4 shadow-sm', className].join(' ')}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {onToggleCollapse ? (
            <Tooltip label="Hide filters" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onToggleCollapse}
                aria-label="Hide filters"
              >
                <IconLayoutSidebarLeftCollapse size={18} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          <span className="text-base font-semibold text-slate-800">Filters</span>
        </div>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={() => onFiltersChange(getDefaultFilters())}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Clear all
          </button>
        ) : null}
      </div>

      <div className="mt-2">
        <FilterSection title="Visit Status" defaultOpen>
          <CheckboxList
            options={VISIT_STATUS_OPTIONS}
            selected={filters.visitStatuses}
            onChange={(next) => onFiltersChange({ ...filters, visitStatuses: next })}
          />
        </FilterSection>

        <FilterSection title="Entity" defaultOpen>
          <CheckboxList
            options={BILLING_ENTITY_OPTIONS}
            selected={filters.billingEntities}
            onChange={(next) => onFiltersChange({ ...filters, billingEntities: next })}
          />
        </FilterSection>

        <FilterSection title="Progress Note" defaultOpen>
          <CheckboxList
            options={PROGRESS_NOTE_OPTIONS}
            selected={filters.progressNoteStatuses}
            onChange={(next) => onFiltersChange({ ...filters, progressNoteStatuses: next })}
          />
        </FilterSection>

        <FilterSection title="Claim Status" defaultOpen>
          <CheckboxList
            options={CLAIM_STATUS_OPTIONS}
            selected={filters.claimStatuses}
            onChange={(next) => onFiltersChange({ ...filters, claimStatuses: next })}
          />
        </FilterSection>

        <FilterSection title="Routing" defaultOpen>
          <CheckboxList
            options={ROUTING_OPTIONS}
            selected={filters.submissionRoutes}
            onChange={(next) => onFiltersChange({ ...filters, submissionRoutes: next })}
          />
        </FilterSection>

        <FilterSection title="Payment Status" defaultOpen>
          <CheckboxList
            options={PAYMENT_STATUS_FILTER_OPTIONS}
            selected={filters.paymentStatuses}
            onChange={(next) => onFiltersChange({ ...filters, paymentStatuses: next })}
          />
        </FilterSection>

        <FilterSection title="Needs Attention" defaultOpen>
          <label className="flex cursor-pointer items-center justify-between gap-2 text-sm text-slate-700">
            <span>Needs attention</span>
            <Checkbox
              size="xs"
              aria-label="Needs attention"
              checked={filters.needsAttention}
              onChange={(event) =>
                onFiltersChange({ ...filters, needsAttention: event.currentTarget.checked })
              }
            />
          </label>
        </FilterSection>

        <FilterSection title="Date Range" defaultOpen>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="w-10 text-xs font-medium text-slate-500">From</span>
              <DatePickerInput
                placeholder="MM/DD/YYYY"
                value={filters.dateFrom}
                onChange={(value) => value && onFiltersChange({ ...filters, dateFrom: value })}
                valueFormat="MM/DD/YYYY"
                rightSectionPointerEvents="none"
                rightSection={<IconCalendar size={16} className="text-slate-500" />}
                classNames={DATE_INPUT_CLASSNAMES}
                aria-label="Filter from date"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-10 text-xs font-medium text-slate-500">To</span>
              <DatePickerInput
                placeholder="MM/DD/YYYY"
                value={filters.dateTo}
                onChange={(value) => value && onFiltersChange({ ...filters, dateTo: value })}
                valueFormat="MM/DD/YYYY"
                rightSectionPointerEvents="none"
                rightSection={<IconCalendar size={16} className="text-slate-500" />}
                classNames={DATE_INPUT_CLASSNAMES}
                aria-label="Filter to date"
              />
            </div>
            {rangeError ? (
              <Text size="xs" c="red">
                {rangeError}
              </Text>
            ) : null}
          </div>
        </FilterSection>
      </div>
    </Paper>
  )
}
